import React, { useState, useEffect } from 'react';
import { AiOutlineCloudUpload, AiOutlineFile, AiOutlineLoading3Quarters, AiOutlineFileImage, AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { BsFiletypePdf, BsFiletypeDocx, BsFiletypeTxt, BsFiletypeJson, BsFiletypeXlsx } from 'react-icons/bs';
import pdfToText from 'react-pdftotext';
import { Document, Packer, Paragraph } from 'docx';
import * as XLSX from 'xlsx';
import { OpenAI } from 'openai';
import JSZip from 'jszip';

function FileUpload({ onFileUpload, disabled, onExtractingStateChange }) {
  const [dragging, setDragging] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);
  
  // Added for progress tracking
  const [processingStatus, setProcessingStatus] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(-1);
  const [overallProgress, setOverallProgress] = useState(0);
  
  // Update parent component with extracting state
  useEffect(() => {
    if (onExtractingStateChange) {
      onExtractingStateChange(extracting);
    }
  }, [extracting, onExtractingStateChange]);
  
  // Initialize processing status when selected files change
  useEffect(() => {
    if (selectedFiles.length > 0) {
      setProcessingStatus(
        selectedFiles.map(file => ({
          name: file.name,
          status: 'pending', // 'pending', 'processing', 'success', 'error'
          progress: 0
        }))
      );
      setCurrentFileIndex(-1);
      setOverallProgress(0);
    } else {
      setProcessingStatus([]);
      setCurrentFileIndex(-1);
      setOverallProgress(0);
    }
  }, [selectedFiles]);
  
  // Supported file types
  const supportedTypes = [
    'application/pdf', 
    'text/plain', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/json',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png', 
    'image/gif',
    'image/webp'
  ];
  const maxFileSize = 20 * 1024 * 1024; // 20MB limit
  
  // Handle drag events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setDragging(true);
  };
  
  // Process files for upload
  const processFiles = (files) => {
    if (disabled) return;
    
    setError('');
    const newFiles = Array.from(files);
    
    // Validate file types and sizes
    const validFiles = newFiles.filter(file => {
      // Check file type
      if (!supportedTypes.includes(file.type)) {
        setError('Unsupported file type. Please upload PDF, TXT, DOCX, XLSX, JSON, or image files (JPG, PNG, GIF).');
        return false;
      }
      
      // Check file size
      if (file.size > maxFileSize) {
        setError('File too large. Maximum file size is 20MB.');
        return false;
      }
      
      return true;
    });
    
    setSelectedFiles(validFiles);
  };
  
  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (disabled) return;
    
    const { files } = e.dataTransfer;
    processFiles(files);
  };
  
  // Handle file input change
  const handleFileChange = (e) => {
    const { files } = e.target;
    processFiles(files);
  };
  
  // Get icon for file type
  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'application/pdf':
        return <BsFiletypePdf className="text-red-500" />;
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return <BsFiletypeDocx className="text-blue-500" />;
      case 'text/plain':
        return <BsFiletypeTxt className="text-gray-500" />;
      case 'application/json':
        return <BsFiletypeJson className="text-yellow-500" />;
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return <BsFiletypeXlsx className="text-green-500" />;
      case 'image/jpeg':
      case 'image/png':
      case 'image/gif':
      case 'image/webp':
        return <AiOutlineFileImage className="text-purple-500" />;
      default:
        return <AiOutlineFile className="text-gray-400" />;
    }
  };
  
  // Helper function to update processing status for a file
  const updateFileStatus = (index, status, progress = null) => {
    setProcessingStatus(prevStatus => {
      const newStatus = [...prevStatus];
      newStatus[index] = {
        ...newStatus[index],
        status,
        ...(progress !== null && { progress })
      };
      return newStatus;
    });
  };
  
  // Extract text from PDF files
  const extractTextFromPDF = async (file) => {
    try {
      const text = await pdfToText(file);
      return {
        originalFile: file,
        text: text,
        name: file.name,
        type: file.type
      };
    } catch (error) {
      console.error('Error extracting text from PDF:', error);
      throw new Error(`Failed to extract text from ${file.name}`);
    }
  };
  
  // Read text from text files
  const extractTextFromTxt = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({
          originalFile: file,
          text: e.target.result,
          name: file.name,
          type: file.type
        });
      };
      reader.onerror = (e) => {
        reject(new Error(`Failed to read ${file.name}`));
      };
      reader.readAsText(file);
    });
  };
  
  // Process JSON files
  const extractTextFromJSON = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          // Try to parse JSON to ensure it's valid
          JSON.parse(e.target.result);
          resolve({
            originalFile: file,
            text: e.target.result,
            name: file.name,
            type: file.type
          });
        } catch (error) {
          reject(new Error(`Invalid JSON file: ${file.name}`));
        }
      };
      reader.onerror = (e) => {
        reject(new Error(`Failed to read ${file.name}`));
      };
      reader.readAsText(file);
    });
  };
  
  // Extract text from DOCX files
  const extractTextFromDOCX = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const text = await extractTextFromDocxArrayBuffer(arrayBuffer);
          
          resolve({
            originalFile: file,
            text: text,
            name: file.name,
            type: file.type
          });
        } catch (error) {
          console.error('Error extracting text from DOCX:', error);
          reject(new Error(`Failed to extract text from ${file.name}`));
        }
      };
      reader.onerror = (e) => {
        reject(new Error(`Failed to read ${file.name}`));
      };
      reader.readAsArrayBuffer(file);
    });
  };
  
  // Helper function to extract text from DOCX array buffer
  const extractTextFromDocxArrayBuffer = async (arrayBuffer) => {
    try {
      // Use JSZip to extract content from the DOCX file (which is a ZIP archive)
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(arrayBuffer);
      
      // Extract the document.xml file which contains the main text content
      const contentXml = await zipContent.file('word/document.xml')?.async('text');
      
      if (!contentXml) {
        return "No text content found in DOCX file";
      }
      
      // Create a DOM parser to parse the XML content
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(contentXml, 'text/xml');
      
      // Extract all text elements (w:t tags)
      const textElements = xmlDoc.getElementsByTagName('w:t');
      
      // Combine text elements with proper spacing
      let extractedText = '';
      let lastWasSpace = false;
      
      for (let i = 0; i < textElements.length; i++) {
        const textNode = textElements[i];
        
        // Get parent paragraph element
        let paragraphNode = textNode.parentNode;
        while (paragraphNode && paragraphNode.nodeName !== 'w:p') {
          paragraphNode = paragraphNode.parentNode;
        }
        
        // Add the text content
        const text = textNode.textContent;
        
        if (text && text.trim()) {
          if (i > 0 && !lastWasSpace) {
            extractedText += ' ';
          }
          extractedText += text;
          lastWasSpace = text.endsWith(' ');
        }
      }
      
      // Add paragraph breaks by scanning for paragraph elements
      const paragraphs = xmlDoc.getElementsByTagName('w:p');
      let finalText = '';
      
      if (paragraphs.length > 0) {
        // Use paragraphs to structure the text properly
        for (let i = 0; i < paragraphs.length; i++) {
          const paragraph = paragraphs[i];
          const paragraphTexts = paragraph.getElementsByTagName('w:t');
          let paragraphText = '';
          
          for (let j = 0; j < paragraphTexts.length; j++) {
            paragraphText += paragraphTexts[j].textContent;
          }
          
          if (paragraphText.trim()) {
            finalText += paragraphText.trim() + '\n\n';
          }
        }
      } else {
        // If no paragraphs found, use the raw extracted text
        finalText = extractedText;
      }
      
      return finalText.trim() || "No text content found in DOCX file";
    } catch (error) {
      console.error('Error in DOCX text extraction:', error);
      return "Error extracting text from DOCX file: " + error.message;
    }
  };
  
  // Extract data from XLSX files
  const extractTextFromXLSX = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Extract text from each sheet
          let extractedText = '';
          workbook.SheetNames.forEach(sheetName => {
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            
            if (jsonData.length > 0) {
              extractedText += `Sheet: ${sheetName}\n`;
              
              // Format as a table with rows and columns
              jsonData.forEach((row, rowIndex) => {
                if (Array.isArray(row) && row.length > 0) {
                  extractedText += row.join('\t') + '\n';
                }
              });
              
              extractedText += '\n';
            }
          });
          
          resolve({
            originalFile: file,
            text: extractedText || "No data found in Excel file",
            name: file.name,
            type: file.type
          });
        } catch (error) {
          console.error('Error extracting data from XLSX:', error);
          reject(new Error(`Failed to extract data from ${file.name}`));
        }
      };
      reader.onerror = (e) => {
        reject(new Error(`Failed to read ${file.name}`));
      };
      reader.readAsArrayBuffer(file);
    });
  };
  
  // Extract text from images using GPT-4o mini vision
  const extractTextFromImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // Get the base64 representation of the image
          const base64Image = e.target.result.split(',')[1];
          
          // Initialize the OpenAI client
          const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
          if (!apiKey) {
            throw new Error('OpenAI API key not found. Please add it to your .env file.');
          }
          
          const openai = new OpenAI({
            apiKey: apiKey,
            dangerouslyAllowBrowser: true // Required for client-side usage
          });
          
          // Call the OpenAI API to extract text from the image
          const response = await openai.chat.completions.create({
            model: "gpt-4o-mini", // Use the GPT-4o mini model with vision
            messages: [
              {
                role: "user",
                content: [
                  { 
                    type: "text", 
                    text: "Please extract all the text from this image. Format it clearly, preserving paragraphs and structure." 
                  },
                  {
                    type: "image_url",
                    image_url: {
                      url: `data:${file.type};base64,${base64Image}`,
                      detail: "high" // Request high detail for better text extraction
                    }
                  }
                ]
              }
            ],
            max_tokens: 500
          });
          
          // Get the extracted text from the response
          const extractedText = response.choices[0].message.content;
          
          resolve({
            originalFile: file,
            text: extractedText || "No text found in image",
            name: file.name,
            type: file.type
          });
        } catch (error) {
          console.error('Error extracting text from image:', error);
          reject(new Error(`Failed to extract text from ${file.name}: ${error.message}`));
        }
      };
      reader.onerror = (e) => {
        reject(new Error(`Failed to read ${file.name}`));
      };
      reader.readAsDataURL(file); // Read as data URL for base64 conversion
    });
  };
  
  // Process the selected files
  const processFile = async (file, index) => {
    try {
      updateFileStatus(index, 'processing', 30);
      
      let result;
      if (file.type === 'application/pdf') {
        updateFileStatus(index, 'processing', 40);
        result = await extractTextFromPDF(file);
      } else if (file.type === 'text/plain') {
        updateFileStatus(index, 'processing', 50);
        result = await extractTextFromTxt(file);
      } else if (file.type === 'application/json') {
        updateFileStatus(index, 'processing', 60);
        result = await extractTextFromJSON(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        updateFileStatus(index, 'processing', 50);
        result = await extractTextFromDOCX(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        updateFileStatus(index, 'processing', 60);
        result = await extractTextFromXLSX(file);
      } else if (['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        updateFileStatus(index, 'processing', 40);
        result = await extractTextFromImage(file);
      } else {
        throw new Error(`Unsupported file type: ${file.type}`);
      }
      
      updateFileStatus(index, 'success', 100);
      return result;
    } catch (error) {
      console.error('Error processing file:', error);
      updateFileStatus(index, 'error', 100);
      throw error;
    }
  };
  
  // Process files sequentially with progress tracking
  const processAllFiles = async () => {
    setExtracting(true);
    setError('');
    setProcessedFiles([]);
    
    const processedResults = [];
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        setCurrentFileIndex(i);
        
        // Update overall progress
        const overallPercent = Math.floor((i / selectedFiles.length) * 100);
        setOverallProgress(overallPercent);
        
        try {
          const result = await processFile(selectedFiles[i], i);
          processedResults.push(result);
        } catch (error) {
          console.error(`Error processing ${selectedFiles[i].name}:`, error);
          setError(prev => 
            (prev ? prev + "\n" : "") + 
            `Failed to process ${selectedFiles[i].name}: ${error.message}`
          );
        }
        
        // Simulate a small delay between processing files to show progress
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Set overall progress to 100% when done
      setOverallProgress(100);
      setCurrentFileIndex(-1);
      
      if (processedResults.length === 0) {
        throw new Error('Failed to process any files');
      }
      
      setProcessedFiles(processedResults);
      return processedResults;
    } catch (error) {
      console.error('Error processing files:', error);
      setError(`Processing failed: ${error.message}`);
      throw error;
    } finally {
      setExtracting(false);
    }
  };
  
  // Upload selected files
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || disabled || extracting) return;
    
    try {
      const processedFiles = await processAllFiles();
      
      // Call the onFileUpload callback provided by parent with processed files
      onFileUpload(processedFiles);
      
      // Reset selected files after successful upload
      setSelectedFiles([]);
      setProcessedFiles([]);
      setProcessingStatus([]);
      setCurrentFileIndex(-1);
      setOverallProgress(0);
    } catch (error) {
      console.error('Error processing and uploading files:', error);
      setError(`Upload failed: ${error.message}`);
    }
  };
  
  // Clear selected files
  const handleClear = () => {
    setSelectedFiles([]);
    setProcessedFiles([]);
    setError('');
    setProcessingStatus([]);
    setCurrentFileIndex(-1);
    setOverallProgress(0);
  };
  
  // Function to get status icon based on file status
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return null;
      case 'processing':
        return <AiOutlineLoading3Quarters className="animate-spin text-indigo-500" />;
      case 'success':
        return <AiOutlineCheck className="text-green-500" />;
      case 'error':
        return <AiOutlineClose className="text-red-500" />;
      default:
        return null;
    }
  };
  
  return (
    <div className="w-full mb-3">
      {/* File drop area */}
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all duration-300 ${
          dragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-300'
        } ${disabled || extracting ? 'opacity-50 cursor-not-allowed' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && !extracting && document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          multiple
          className="hidden"
          onChange={handleFileChange}
          accept=".pdf,.txt,.docx,.json,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
          disabled={disabled || extracting}
        />
        <AiOutlineCloudUpload className="mx-auto h-10 w-10 text-indigo-500" />
        <p className="mt-2 text-sm text-gray-600">
          Drop files here or click to upload
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Supports PDF, TXT, DOCX, XLSX, JSON, Images (JPG, PNG, GIF) (Max 20MB)
        </p>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mt-2 text-sm text-red-600 whitespace-pre-line">
          {error}
        </div>
      )}
      
      {/* Selected files */}
      {selectedFiles.length > 0 && (
        <div className="mt-3">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium text-gray-700">
              Selected Files ({selectedFiles.length})
            </h3>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-gray-500 hover:text-gray-700"
              disabled={extracting || disabled}
            >
              Clear All
            </button>
          </div>
          
          {/* Overall progress bar */}
          {extracting && (
            <div className="mb-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Overall Progress</span>
                <span>{overallProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300" 
                  style={{ width: `${overallProgress}%` }}>
                </div>
              </div>
            </div>
          )}
          
          {/* File list with status */}
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden max-h-60 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <li 
                key={index} 
                className={`px-3 py-2 flex items-center justify-between bg-white ${
                  currentFileIndex === index && extracting ? 'bg-indigo-50' : ''
                }`}
              >
                <div className="flex items-center flex-grow mr-2">
                  {getFileIcon(file.type)}
                  <span className="ml-2 text-sm text-gray-700 truncate max-w-xs">
                    {file.name}
                  </span>
                </div>
                
                <div className="flex items-center">
                  {/* File size */}
                  <span className="text-xs text-gray-500 mr-2">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                  
                  {/* Processing status indicators */}
                  {processingStatus[index] && (
                    <div className="flex items-center">
                      {processingStatus[index].status === 'processing' && (
                        <div className="w-16 bg-gray-200 rounded-full h-1.5 mr-2">
                          <div 
                            className="bg-indigo-600 h-1.5 rounded-full" 
                            style={{ width: `${processingStatus[index].progress}%` }}>
                          </div>
                        </div>
                      )}
                      <span className="w-5 h-5 flex items-center justify-center">
                        {getStatusIcon(processingStatus[index].status)}
                      </span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          
          {/* Process button */}
          <button
            type="button"
            onClick={handleUpload}
            disabled={extracting || disabled}
            className={`mt-3 w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
              ${extracting || disabled
                ? 'bg-indigo-300 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
          >
            {extracting ? (
              <>
                <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                Processing Files ({currentFileIndex + 1}/{selectedFiles.length})...
              </>
            ) : (
              'Process Files'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default FileUpload; 